import { TextField, SxProps } from '@mui/material';
import { BaseTheme, mergeSx } from 'design-system';
import { useState, useEffect } from 'react';
import dayjs from 'services/dayjs';
import { Field } from 'generated/graphql';

interface DatePickerFieldProps {
  field: Field;
  className?: string;
  sx?: SxProps<BaseTheme>;
  hasWhiteFields?: boolean;
  autoFocus?: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  onChange(value: string, values?: string[], entityID?: string): void;
}

export const DatePickerField = ({
  field,
  className,
  sx,
  hasWhiteFields = false,
  autoFocus,
  isDisabled = false,
  isReadOnly = false,
  onChange,
}: DatePickerFieldProps) => {
  // Local state for the date input to preserve user-typed value until it's valid,
  // prevents premature formatting or clearing during user input
  const [localDate, setLocalDate] = useState(() => field.value || '');

  useEffect(() => {
    setLocalDate(field.value || '');
  }, [field.value]);

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setLocalDate(newValue);

    // Only call the onChange if the new date is valid
    if (dayjs(newValue, 'YYYY-MM-DD', true).isValid()) {
      onChange(newValue);
    }
  };

  return (
    <TextField
      className={className}
      sx={mergeSx(sx ?? {}, (theme) => ({
        background: hasWhiteFields ? theme.palette.background.paper : 'initial',
        borderRadius: theme.spacing(1),
      }))}
      variant="outlined"
      margin="dense"
      autoFocus={autoFocus}
      fullWidth
      id={field.id.toString()}
      type={field.type}
      label={field.label}
      required={field.required}
      slotProps={{ htmlInput: { readOnly: isReadOnly }, inputLabel: { shrink: true } }}
      disabled={isDisabled}
      value={localDate}
      onChange={handleDateChange}
      onBlur={() => {
        // If the local date is not a valid YYYY-MM-DD date, then clear the date
        if (!dayjs(localDate, 'YYYY-MM-DD', true).isValid()) {
          onChange('');
        }
      }}
    />
  );
};

export default DatePickerField;
